import { useState } from "react";
import { useCashReport } from "@/api/cash";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { formatDate, formatMoney } from "@/lib/format";
import { formatTotals } from "@/lib/money";

const TH = "px-4 py-2 font-medium";

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0 rounded-lg border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-lg font-semibold text-primary">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function CashPanel() {
  const [asOf, setAsOf] = useState("");
  const report = useCashReport(asOf || undefined);
  if (report.isPending) return <p className="text-muted-foreground">Loading…</p>;
  if (report.isError) return <RequestError error={report.error} />;
  const data = report.data;
  const total = (rows: { currency: string; amount: string }[]) =>
    rows.length ? formatTotals(rows) : "Nothing";
  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="cash-as-of"
        label="As of"
        type="date"
        value={asOf || data.as_of}
        onChange={setAsOf}
        className="sm:w-48"
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="At hand" value={total(data.totals)} hint="Bank and cash accounts" />
        <Figure
          label="Held at the sister company"
          value={total(data.held_at_sister)}
          hint="To recover"
        />
        <Figure
          label="Owed to directors"
          value={total(data.owed_to_directors)}
          hint="What they paid from their own pocket"
        />
        <Figure
          label="Pending transfers"
          value={String(data.pending_transfers)}
          hint="Instructed, not yet received"
        />
      </div>
      {data.not_converted.length > 0 && (
        <Alert variant="warning" title={`${formatTotals(data.not_converted)} is not placed`}>
          Money in a currency its account does not hold, with no rupee figure; it is left out of
          every balance rather than converted at a guess.
        </Alert>
      )}
      <div className="table-frame">
        <table className="data-table">
          <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className={TH}>Account</th>
              <th className={`${TH} text-right`}>Opening</th>
              <th className={`${TH} text-right`}>Received</th>
              <th className={`${TH} text-right`}>Transfers in</th>
              <th className={`${TH} text-right`}>Transfers out</th>
              <th className={`${TH} text-right`}>Spent</th>
              <th className={`${TH} text-right`}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.accounts.map((row) => (
              <tr key={row.id} className="border-b last:border-0">
                <td className="px-4 py-2">{row.name}</td>
                {[row.opening, row.received, row.transfers_in, row.transfers_out, row.spent].map(
                  (v, i) => (
                    <td key={i} className="px-4 py-2 text-right tabular-nums">
                      {formatMoney(v, row.currency)}
                    </td>
                  ),
                )}
                <td className="px-4 py-2 text-right font-semibold tabular-nums">
                  {formatMoney(row.balance, row.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Balances as of {formatDate(data.as_of)}; pending transfers are not in them.
        {data.unplaced_payments + data.unplaced_expenses > 0 &&
          ` ${data.unplaced_payments} payment(s) and ${data.unplaced_expenses} expense(s) name no account and are left out.`}
      </p>
    </div>
  );
}
